/**
 * Instant search (<predictive-search>), markup in snippets/instant-search.liquid.
 *
 * States, as attributes on the element (styled by component-predictive-search.css):
 *   open     the panel is visible
 *   results  the results container holds the markup of sections/predictive-search.liquid
 *            for the current term (otherwise the idle content is shown)
 *   loading  a request is in flight
 *
 * The results and the "view all" counts are cached per normalized term and shared by
 * every instance on the page.
 */
const INSTANT_SEARCH_CACHE_LIMIT = 40;
const INSTANT_SEARCH_MOBILE = window.matchMedia('(max-width: 1024px)');
const instantSearchResultsCache = new Map();
const instantSearchCountCache = new Map();

function instantSearchCacheSet(cache, key, value) {
  if (cache.size >= INSTANT_SEARCH_CACHE_LIMIT) cache.delete(cache.keys().next().value);
  cache.set(key, value);
}

function instantSearchDebounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-instant-search-panel]');
    this.scroller = this.querySelector('[data-instant-search-body]') || this.panel;
    this.idle = this.querySelector('[data-instant-search-idle]');
    this.results = this.querySelector('[data-instant-search-results]');
    this.status = this.querySelector('[data-instant-search-status]');
    this.resources = this.dataset.resources || 'query,product,collection';
    this.searchTerm = '';
    this.activeOption = null;
    this.abortController = null;

    // header section hosting the search and its child to pin while the page is locked (mobile)
    this.header = this.closest('.shopify-section');
    this.headerPin = this.header ? Array.from(this.header.children).find((child) => child.contains(this)) : null;
    this.pageLocked = false;
    this.lockedScrollY = 0;
    this.scrollYBeforeFocus = null;
    this.headerTop = 0;
    this.headerHeight = 0;

    this.onInput = instantSearchDebounce(() => this.sync(), 250);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onLockedScroll = this.onLockedScroll.bind(this);
    this.updateOffset = this.updateOffset.bind(this);
  }

  connectedCallback() {
    if (!this.input || !this.panel || !this.results) return;

    this.assignOptionIds();
    this.syncAriaControls();
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('pointerdown', () => {
      if (!this.pageLocked) this.scrollYBeforeFocus = window.scrollY;
    });
    this.input.addEventListener('focus', () => this.sync());
    this.input.addEventListener('click', () => this.sync());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', (event) => this.onFocusOut(event));
    this.querySelector('[data-instant-search-clear]')?.addEventListener('click', () => this.clear());
    this.querySelectorAll('[data-instant-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    // touching the panel dismisses the virtual keyboard, the panel stays open
    this.panel.addEventListener('touchstart', () => this.input.blur(), { passive: true });
  }

  disconnectedCallback() {
    this.close();
    this.abort();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get isLoading() {
    return this.hasAttribute('loading');
  }

  get hasResults() {
    return this.hasAttribute('results');
  }

  get query() {
    return this.input.value.trim();
  }

  /* The listbox currently shown: the results of a term, the idle content otherwise. */
  get activeList() {
    return this.hasResults ? this.results : this.idle;
  }

  get options() {
    return Array.from(this.activeList?.querySelectorAll('[role="option"]') ?? []);
  }

  /* ---------- events ---------- */

  /* Aligns the panel with the input value: idle content, cached/new results or just open. */
  sync() {
    const term = this.query;

    if (!term) return this.showIdle();
    if (term !== this.searchTerm) return this.search(term);
    if (this.hasResults || this.isLoading) return this.open();
    this.search(term);
  }

  onSubmit(event) {
    if (!this.query) event.preventDefault();
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) this.sync();
        this.moveActiveOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveOption(-1);
        break;
      case 'Enter':
        if (this.isOpen && this.activeOption) {
          event.preventDefault();
          this.activeOption.querySelector('a')?.click();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
        } else if (this.input.value) {
          this.clear();
        }
        break;
    }
  }

  onFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
  }

  onDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.close();
  }

  /* ---------- panel ---------- */

  open() {
    if (!this.activeList && !this.isLoading) return;

    if (this.shouldLockPage) this.lockPage();
    this.updateOffset();
    this.syncAriaControls();
    if (this.isOpen) return;

    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    window.addEventListener('resize', this.updateOffset);
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.setActiveOption(null);
    this.unlockPage();
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    window.removeEventListener('resize', this.updateOffset);
  }

  /* ---------- page lock (mobile) ----------
     The sheet covers the page below the header, so the page must not scroll under it: the
     scroll position is frozen where it is, the header content is pinned where it shows at
     that moment (pulled fully into view if it was partly scrolled off, never moved otherwise,
     so a pre-header above it neither appears nor scrolls away), touch scrolling is confined
     to the panel body and any other scroll is reverted. */

  lockPage() {
    if (this.pageLocked) return;
    this.pageLocked = true;

    // focusing the input may already have scrolled the page: go back to where the tap happened
    if (this.scrollYBeforeFocus !== null && this.scrollYBeforeFocus !== window.scrollY) {
      window.scrollTo({ top: this.scrollYBeforeFocus, behavior: 'instant' });
    }
    this.scrollYBeforeFocus = null;
    this.lockedScrollY = window.scrollY;

    const root = document.documentElement;
    root.classList.add('instant-search-lock');
    if (this.header && this.headerPin) {
      // the section keeps the height of its content in the flow while the content is pinned
      // exactly where it shows now; the sheet starts right under it (see updateOffset)
      this.headerHeight = this.headerPin.offsetHeight;
      this.headerTop = Math.max(0, Math.round(this.headerPin.getBoundingClientRect().top));
      root.style.setProperty('--instant-search-header-height', `${this.headerHeight}px`);
      root.style.setProperty('--instant-search-header-top', `${this.headerTop}px`);
      this.header.classList.add('instant-search-lock__header');
      this.headerPin.classList.add('instant-search-lock__pin');
      // theme.js (initHeader) gives every header section an inline z-index (~21): override it inline
      // so the sheet stacks above the page's fixed bars (toolbar, recently viewed, overlay…)
      this.headerZIndex = this.header.style.zIndex;
      this.header.style.zIndex = '102';
    }
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('scroll', this.onLockedScroll);
  }

  unlockPage() {
    this.scrollYBeforeFocus = null;
    if (!this.pageLocked) return;
    this.pageLocked = false;

    const root = document.documentElement;
    root.classList.remove('instant-search-lock');
    root.style.removeProperty('--instant-search-header-top');
    root.style.removeProperty('--instant-search-header-height');
    if (this.header) {
      this.header.classList.remove('instant-search-lock__header');
      this.header.style.zIndex = this.headerZIndex ?? '';
    }
    this.headerPin?.classList.remove('instant-search-lock__pin');
    document.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('scroll', this.onLockedScroll);
  }

  onTouchMove(event) {
    const insideScrollablePanel = this.scroller.contains(event.target) && this.scroller.scrollHeight > this.scroller.clientHeight;
    if (!insideScrollablePanel) event.preventDefault();
  }

  onLockedScroll() {
    if (window.scrollY !== this.lockedScrollY) window.scrollTo(0, this.lockedScrollY);
  }

  /* Back to the idle content (trending searches and popular products). */
  showIdle() {
    this.abort();
    this.searchTerm = '';
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.results.replaceChildren();
    this.setActiveOption(null);

    if (this.idle) {
      this.open();
    } else {
      this.close();
    }
  }

  clear() {
    this.input.value = '';
    this.showIdle();
    this.input.focus();
  }

  /* --instant-search-top: where the sheet starts on mobile (right under the pinned header) and,
     on desktop, the bottom of the form the dropdown height is capped from.
     Also re-evaluates the page lock when the viewport crosses the breakpoint. */
  updateOffset() {
    if (this.isOpen) {
      if (this.shouldLockPage) {
        this.lockPage();
      } else {
        this.unlockPage();
      }
    }

    let top;
    if (this.pageLocked && this.headerPin) {
      // the pinned header may change height (orientation, fonts): keep the section and the sheet in sync
      this.headerHeight = this.headerPin.offsetHeight;
      document.documentElement.style.setProperty('--instant-search-header-height', `${this.headerHeight}px`);
      top = this.headerTop + this.headerHeight;
    } else {
      top = this.form.getBoundingClientRect().bottom;
    }
    this.style.setProperty('--instant-search-top', `${Math.round(top)}px`);
  }

  /* The sheet covers the page only when the search sits in a header section on mobile. */
  get shouldLockPage() {
    return INSTANT_SEARCH_MOBILE.matches && this.header !== null;
  }

  syncAriaControls() {
    const list = this.activeList;
    if (!list) return;

    if (!list.id) list.id = `${this.input.id}-${list === this.results ? 'results' : 'idle'}`;
    this.input.setAttribute('aria-controls', list.id);
  }

  /* ---------- keyboard navigation ---------- */

  assignOptionIds() {
    this.querySelectorAll('[role="option"]').forEach((option, index) => {
      option.id = `${this.input.id}-option-${index}`;
    });
  }

  moveActiveOption(step) {
    const options = this.options;
    if (!options.length) return;

    const current = options.indexOf(this.activeOption);
    const next = current === -1 && step < 0 ? options.length - 1 : (current + step + options.length) % options.length;
    this.setActiveOption(options[next]);
  }

  setActiveOption(option) {
    this.activeOption?.setAttribute('aria-selected', 'false');
    this.activeOption = option;

    if (option) {
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  /* ---------- results ---------- */

  abort() {
    this.abortController?.abort();
    this.abortController = null;
  }

  static normalize(term) {
    return term.toLowerCase().replace(/\s+/g, ' ');
  }

  search(term) {
    this.searchTerm = term;
    this.setActiveOption(null);
    this.abort();

    const key = PredictiveSearch.normalize(term);
    const cached = instantSearchResultsCache.get(key);
    if (cached !== undefined) {
      this.render(cached, term);
      return;
    }

    this.abortController = new AbortController();
    this.setAttribute('loading', '');
    this.announce(this.dataset.loadingText);
    this.open();

    const params = new URLSearchParams({
      q: term,
      section_id: 'predictive-search',
      'resources[type]': this.resources,
    });

    fetch(`${routes.predictive_search_url}?${params}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
        const markup = section?.innerHTML ?? '';
        instantSearchCacheSet(instantSearchResultsCache, key, markup);
        if (this.searchTerm === term) this.render(markup, term);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.removeAttribute('loading');
        console.error(error);
      });
  }

  render(markup, term) {
    this.results.innerHTML = markup;
    this.removeAttribute('loading');
    this.setAttribute('results', '');
    this.assignOptionIds();
    // a response arriving after the customer clicked away must not reopen the panel
    if (this.isOpen || this.contains(document.activeElement)) this.open();
    this.scroller.scrollTop = 0;
    this.announce(this.results.querySelector('[data-instant-search-status-text]')?.textContent.trim());
    this.loadResultsCount(term);
    this.afterRender();
  }

  /* Fills "View all results (N)" with the total of the search page, fetched as a tiny
     section (sections/search-results-count.liquid) instead of the whole page. */
  loadResultsCount(term) {
    const target = this.results.querySelector('[data-instant-search-count]');
    if (!target) return;

    const key = PredictiveSearch.normalize(term);
    const apply = (count) => {
      if (this.searchTerm !== term) return;
      target.textContent = `(${count})`;
      target.hidden = false;
    };

    if (instantSearchCountCache.has(key)) {
      apply(instantSearchCountCache.get(key));
      return;
    }

    const params = new URLSearchParams({
      q: term,
      'options[prefix]': 'last',
      type: 'product',
      section_id: 'search-results-count',
    });

    fetch(`${routes.search_url}?${params}`, { signal: this.abortController?.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Search count failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-results-count]')?.dataset.searchResultsCount;
        if (count === undefined) return;
        instantSearchCacheSet(instantSearchCountCache, key, count);
        apply(count);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.warn(error);
      });
  }

  /* Theme hooks for the product cards injected in the results. */
  afterRender() {
    if ((window.show_multiple_currencies || window.show_auto_currency) && typeof Currency !== 'undefined') {
      const currency = document.querySelector('#currencies .active')?.dataset.currency;
      if (currency) Currency.convertAll(window.shop_currency, currency, 'span.money', 'money_format');
    }

    window.sharedFunctions?.swapHoverVideoProductCard?.();
    if (document.body.classList.contains('product-card-layout-08')) {
      window.sharedFunctions?.productCountdownCard?.();
    }

    this.dispatchEvent(new CustomEvent('predictive-search:render', { bubbles: true, detail: { results: this.results } }));
  }

  announce(text) {
    if (!this.status || !text) return;
    this.status.textContent = text;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}